import React, { useRef } from 'react';
import { CheckCircleIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import type { Movimentacao } from '../../../models';
import { useMovimentacaoMutations } from '../../../hooks/data';

interface TermoAssinadoSectionProps {
  solicitacaoId: string;
  movimentacao: Movimentacao;
  /** Colaborador da Solicitação — dono do termo na ficha (null na Admissão ainda sem vínculo;
   * aí vale só pela Movimentação da entrega). */
  colaboradorId: string | null;
}

/**
 * Último passo do Termo: o PDF assinado pelos dois lados (D4Sign) volta e o SI anexa aqui.
 * Obrigatório pra concluir (concluirSolicitacao), vai pra ficha de cada equipamento da
 * Movimentação e é o único termo que o colaborador vê em "Meus equipamentos".
 */
export default function TermoAssinadoSection({ solicitacaoId, movimentacao, colaboradorId }: TermoAssinadoSectionProps) {
  const { user } = useAuth();
  const { data: colaboradores = [] } = useCollaboratorsPublicQuery();
  const dono = colaboradores.find((c) => c.id === colaboradorId);
  const { anexarTermoAssinado } = useMovimentacaoMutations(solicitacaoId);
  const inputRef = useRef<HTMLInputElement>(null);
  const termo = movimentacao.termoAssinado;

  const enviar = (arquivo: File | undefined) => {
    if (!arquivo || !user) return;
    anexarTermoAssinado.mutate(
      {
        movimentacao,
        arquivo,
        autor: { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '' },
        dono: colaboradorId ? { id: colaboradorId, name: dono?.name ?? '' } : null,
      },
      { onSettled: () => inputRef.current && (inputRef.current.value = '') }
    );
  };

  return (
    <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3 text-sm">
      {termo ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <CheckCircleIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
          <span className="font-medium text-gray-900">Termo assinado</span>
          <a href={termo.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
            {termo.nome}
          </a>
          <span className="text-xs text-gray-500">
            anexado em {termo.anexadoEm.toDate().toLocaleDateString('pt-BR')} por {termo.anexadoPor.name}
          </span>
          <button
            type="button"
            className="text-xs font-semibold text-gray-500 hover:text-indigo-600"
            disabled={anexarTermoAssinado.isPending}
            onClick={() => inputRef.current?.click()}
          >
            {anexarTermoAssinado.isPending ? 'Enviando...' : 'Substituir'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-600">
            Depois de assinado pelos dois lados no D4Sign, anexe aqui o PDF assinado. Sem ele a Solicitação não conclui — é
            o termo que o colaborador vê em &quot;Meus equipamentos&quot; e o que vai pra ficha do equipamento.
          </p>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1.5 text-sm"
            disabled={anexarTermoAssinado.isPending}
            onClick={() => inputRef.current?.click()}
          >
            <PaperClipIcon className="h-4 w-4" aria-hidden="true" />
            {anexarTermoAssinado.isPending ? 'Enviando...' : 'Anexar termo assinado'}
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(event) => enviar(event.target.files?.[0])}
      />
    </div>
  );
}
